import React from "react";
import { FiUser } from "react-icons/fi";
import { resolveStorageUrl } from "../Helper/storage";
import { avatarColor, userInitials } from "../Helper/avatar";

/**
 * The one avatar used everywhere in the admin (presence, Users table, account card, activity feed).
 * Priority: an uploaded picture (`avatarKey` → storage) → an external picture URL (Auth0/Google
 * `pictureUrl`) → a coloured initials circle. The circle colour is hashed on `id` (the stable account
 * id) when given, else the name : so distinct accounts sharing a name still differ visually.
 */
export interface UserAvatarProps {
	name?: string | null;
	/** Stable account id (Auth0 sub / local id). Drives the fallback colour so it is per-account. */
	id?: string | null;
	/** Storage key of an uploaded avatar (resolved to a URL). Highest priority. */
	avatarKey?: string | null;
	/** External picture URL (e.g. the Auth0/Google photo). Used when there is no uploaded avatar. */
	pictureUrl?: string | null;
	/** Tailwind size classes for the circle (default h-7 w-7). */
	sizeClass?: string;
	/** Tailwind text-size class for the initials (default text-[10px]); larger for big avatars. */
	textClass?: string;
	/** Extra ring/border classes (e.g. "ring-2 ring-white" for stacked avatars). */
	ringClass?: string;
	/** Optional native tooltip. */
	title?: string;
	className?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
	name,
	id,
	avatarKey,
	pictureUrl,
	sizeClass = "h-7 w-7",
	textClass = "text-[10px]",
	ringClass = "",
	title,
	className = "",
}) => {
	const label = name ?? "";
	const url = avatarKey ? resolveStorageUrl(avatarKey) : pictureUrl || null;
	const tip = title ?? label;
	const base = `${sizeClass} shrink-0 rounded-full ${ringClass} ${className}`;

	if (url) {
		return <img src={url} alt={label} title={tip} className={`${base} object-cover`} />;
	}

	const initials = userInitials(label);
	return (
		<span
			title={tip}
			className={`${base} inline-flex items-center justify-center font-bold ${textClass} ${avatarColor(id || label)}`}
		>
			{initials || <FiUser className="h-3.5 w-3.5" />}
		</span>
	);
};

export default UserAvatar;
